import React, { useEffect, useState, useCallback } from 'react';
import {
  RefreshCw,
  Copy,
  ExternalLink,
  AlertCircle,
  Hash,
  PlusCircle,
  Mail,
  Loader2,
} from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useToast } from '@/components/ui/toaster.jsx';
import TraceabilityTimeline, {
  ICON_OPTIONS,
  iconByName,
} from './TraceabilityTimeline';
import {
  getTraceabilityByAirRequest,
  notifyRequestCreated,
  addStage,
  updateStage,
  removeStage,
  attachFileToStage,
  detachFileFromStage,
} from '../Services/traceabilityService';

/**
 * Sección de Trazabilidad dentro del detalle de la solicitud aérea.
 *
 * Layout de dos columnas:
 *  - Izquierda: formulario para agregar hitos (icono, título,
 *    descripción y la opción de avisarle al cliente).
 *  - Derecha: la línea de tiempo con los hitos ya cargados.
 *
 * El sistema solo siembra el primer hito ("Recepción de solicitud")
 * al crear la solicitud; el resto los arma el operador uno a uno
 * según lo que vaya pasando en la operación.
 */
export default function TraceabilitySection({ airRequestId, clienteSnapshot }) {
  const toast = useToast();
  const [tr, setTr] = useState(null);
  const [loading, setLoading] = useState(false);
  const [notFound, setNotFound] = useState(false);
  const [notifying, setNotifying] = useState(false);
  const [editingStage, setEditingStage] = useState(null);
  const [attachingStage, setAttachingStage] = useState(null);
  const [deletingStage, setDeletingStage] = useState(null);

  const load = useCallback(async () => {
    if (!airRequestId) return;
    setLoading(true);
    setNotFound(false);
    try {
      const r = await getTraceabilityByAirRequest(airRequestId);
      if (r?.success && r?.data) {
        setTr(r.data);
      } else {
        setTr(null);
        setNotFound(true);
      }
    } catch (err) {
      toast.error('Error al cargar la trazabilidad', err?.message);
    } finally {
      setLoading(false);
    }
  }, [airRequestId, toast]);

  useEffect(() => {
    load();
  }, [load]);

  /** Manda el correo de "solicitud registrada" con el número de seguimiento. */
  const onNotifyClient = useCallback(async () => {
    if (!tr?._id) return;
    setNotifying(true);
    try {
      const r = await notifyRequestCreated(tr._id);
      if (r?.success) {
        toast.success('Cliente informado', r?.message);
      } else {
        toast.error('No se pudo enviar el correo', r?.message);
      }
    } catch (err) {
      toast.error('Error al enviar el correo', err?.message);
    } finally {
      setNotifying(false);
    }
  }, [tr?._id, toast]);

  const onUpdateStage = useCallback(
    async (stage, patch) => {
      if (!tr?._id) return;
      try {
        const r = await updateStage(tr._id, stage._id, patch);
        if (r?.success) {
          setTr(r.data);
          toast.success('Hito actualizado');
        } else {
          toast.error('No se pudo actualizar', r?.message);
        }
      } catch (err) {
        toast.error('Error al actualizar el hito', err?.message);
      }
    },
    [tr?._id, toast],
  );

  /**
   * Agrega un hito. Devuelve true solo si se guardó, para que el
   * formulario de la izquierda sepa si limpiarse.
   */
  const onAddStage = useCallback(
    async (dto) => {
      if (!tr?._id) return false;
      try {
        const r = await addStage(tr._id, dto);
        if (r?.success) {
          setTr(r.data);
          // El backend avisa en el mensaje si el correo salió o no.
          if (dto.notificar && r?.aviso && !r.aviso.enviado) {
            toast.error('Hito agregado, pero no se notificó', r?.message);
          } else {
            toast.success('Hito agregado', dto.notificar ? r?.message : undefined);
          }
          return true;
        }
        toast.error('No se pudo agregar el hito', r?.message);
        return false;
      } catch (err) {
        toast.error('Error al agregar el hito', err?.message);
        return false;
      }
    },
    [tr?._id, toast],
  );

  const onEditStage = useCallback((stage) => setEditingStage(stage), []);
  const onDeleteStage = useCallback((stage) => setDeletingStage(stage), []);

  const confirmDeleteStage = useCallback(async () => {
    if (!tr?._id || !deletingStage) return;
    try {
      const r = await removeStage(tr._id, deletingStage._id);
      if (r?.success) {
        setTr(r.data);
        toast.success('Hito eliminado');
      } else {
        toast.error('No se pudo eliminar', r?.message);
      }
    } catch (err) {
      toast.error('Error al eliminar el hito', err?.message);
    } finally {
      setDeletingStage(null);
    }
  }, [tr?._id, deletingStage, toast]);

  const onAttachFile = useCallback((stage) => setAttachingStage(stage), []);

  const onAttachSubmit = useCallback(
    async (fileMeta) => {
      if (!tr?._id || !attachingStage) return;
      try {
        const r = await attachFileToStage(tr._id, attachingStage._id, fileMeta);
        if (r?.success) {
          setTr(r.data);
          toast.success('Archivo adjuntado');
        } else {
          toast.error('No se pudo adjuntar', r?.message);
        }
      } catch (err) {
        toast.error('Error al adjuntar el archivo', err?.message);
      } finally {
        setAttachingStage(null);
      }
    },
    [tr?._id, attachingStage, toast],
  );

  const onDetachFile = useCallback(
    async (stage, fileKey) => {
      if (!tr?._id) return;
      try {
        const r = await detachFileFromStage(tr._id, stage._id, fileKey);
        if (r?.success) {
          setTr(r.data);
          toast.success('Archivo quitado');
        } else {
          toast.error('No se pudo quitar el archivo', r?.message);
        }
      } catch (err) {
        toast.error('Error al quitar el archivo', err?.message);
      }
    },
    [tr?._id, toast],
  );

  const copyLink = useCallback(() => {
    if (!tr?.numero) return;
    const url = `${window.location.origin}/t/${encodeURIComponent(tr.numero)}`;
    try {
      navigator.clipboard.writeText(url);
      toast.success('Link copiado', url);
    } catch {
      toast.info(url);
    }
  }, [tr?.numero, toast]);

  const clienteSinCorreo = !tr?.clienteSnapshot?.correo && !clienteSnapshot?.correo;

  return (
    <section>
      <div className="flex items-center gap-2 mb-3">
        <div className="flex items-center justify-center w-6 h-6 rounded-md bg-primary/10 text-primary">
          <Hash size={13} />
        </div>
        <span className="text-xs font-semibold uppercase tracking-widest text-muted-foreground">
          Trazabilidad
        </span>
        <div className="flex-1 h-px bg-border" />
        <Button
          size="sm"
          variant="outline"
          onClick={load}
          disabled={loading}
          title="Recargar"
        >
          <RefreshCw className={`h-3.5 w-3.5 ${loading ? 'animate-spin' : ''}`} />
        </Button>
      </div>

      {!tr && !loading && notFound && (
        <Card>
          <CardContent className="p-4 text-sm text-muted-foreground flex items-start gap-2">
            <AlertCircle className="h-4 w-4 mt-0.5 shrink-0" />
            <p>
              Esta solicitud no tiene trazabilidad. Se genera automáticamente al
              crear la solicitud, siempre que el cliente tenga un correo
              registrado.
            </p>
          </CardContent>
        </Card>
      )}

      {tr && (
        <>
          {/* Cabecera: número, acceso del cliente y aviso por correo */}
          <Card>
            <CardContent className="p-3 sm:p-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="text-[10px] uppercase font-semibold text-muted-foreground">
                  Número
                </span>
                <code className="px-2 py-0.5 rounded bg-muted text-sm font-mono">
                  {tr.numero}
                </code>
                <span className="text-[11px] text-muted-foreground">
                  · NIT {tr.clienteSnapshot?.nit || '—'}
                </span>
                <a
                  href={`/t/${encodeURIComponent(tr.numero)}`}
                  target="_blank"
                  rel="noreferrer"
                  className="text-primary text-xs inline-flex items-center gap-1 hover:underline"
                  title="Ver como cliente"
                >
                  <ExternalLink className="h-3 w-3" />
                  Vista cliente
                </a>
              </div>
              <div className="flex items-center gap-2">
                <Button size="sm" variant="outline" onClick={copyLink}>
                  <Copy className="h-3.5 w-3.5 mr-1" />
                  Copiar link
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={onNotifyClient}
                  disabled={notifying || clienteSinCorreo}
                  title={
                    clienteSinCorreo
                      ? 'El cliente no tiene correo registrado'
                      : 'Enviarle el número de seguimiento por correo'
                  }
                >
                  {notifying ? (
                    <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" />
                  ) : (
                    <Mail className="h-3.5 w-3.5 mr-1" />
                  )}
                  Informar al cliente
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* Dos columnas: formulario a la izquierda, línea de tiempo a la derecha */}
          <div className="mt-4 grid grid-cols-1 lg:grid-cols-[minmax(300px,360px)_1fr] gap-4 items-start">
            <AddStageForm
              onSubmit={onAddStage}
              clienteSinCorreo={clienteSinCorreo}
              siguienteOrden={(tr.hitos || []).length}
            />
            <div className="min-w-0">
              <TraceabilityTimeline
                hitos={tr.hitos}
                mode="operator"
                onEditStage={onEditStage}
                onDeleteStage={onDeleteStage}
                onAttachFile={onAttachFile}
                onDetachFile={onDetachFile}
              />
            </div>
          </div>
        </>
      )}

      {/* Dialog: editar hito */}
      {editingStage && (
        <StageEditDialog
          open={!!editingStage}
          onOpenChange={(o) => !o && setEditingStage(null)}
          initial={{
            nombre: editingStage.nombre,
            icono: editingStage.icono,
            descripcion: editingStage.descripcion,
          }}
          onSubmit={async (dto) => {
            await onUpdateStage(editingStage, dto);
            setEditingStage(null);
          }}
        />
      )}

      {/* Dialog: adjuntar archivo */}
      {attachingStage && (
        <AttachFileDialog
          stage={attachingStage}
          onClose={() => setAttachingStage(null)}
          onSubmit={onAttachSubmit}
        />
      )}

      {/* Confirm: eliminar / omitir hito */}
      <AlertDialog
        open={!!deletingStage}
        onOpenChange={(o) => !o && setDeletingStage(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>¿Eliminar este hito?</AlertDialogTitle>
            <AlertDialogDescription>
              Se eliminará el hito <strong>{deletingStage?.nombre}</strong> de la
              línea de tiempo. Esta acción no se puede deshacer.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDeleteStage}>Eliminar</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </section>
  );
}

/**
 * Formulario de la columna izquierda. Agrega hitos uno a uno, con
 * selector visual de icono y la opción de avisarle al cliente.
 *
 * El hito no tiene estado: se registra como un evento que ya ocurrió
 * y queda fechado por el backend en ese momento.
 */
export function AddStageForm({ onSubmit, clienteSinCorreo, siguienteOrden = 0 }) {
  const vacio = {
    nombre: '',
    icono: 'Flag',
    descripcion: '',
    notificar: false,
  };
  const [form, setForm] = useState(vacio);
  const [saving, setSaving] = useState(false);

  const IconoPreview = iconByName(form.icono);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.nombre.trim()) return;
    setSaving(true);
    try {
      const ok = await onSubmit({
        nombre: form.nombre.trim(),
        icono: form.icono,
        descripcion: form.descripcion.trim(),
        notificar: form.notificar && !clienteSinCorreo,
      });
      // Solo limpiamos si el backend confirmó; si falló, el operador
      // no pierde lo que había escrito.
      if (ok) setForm(vacio);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card className="lg:sticky lg:top-4">
      <CardContent className="p-4">
        <div className="flex items-center gap-2 mb-3">
          <div className="flex items-center justify-center w-6 h-6 rounded-md bg-primary/10 text-primary">
            <PlusCircle size={13} />
          </div>
          <span className="text-xs font-semibold uppercase tracking-widest text-muted-foreground">
            Agregar hito
          </span>
        </div>
        <p className="text-[11px] text-muted-foreground mb-3">
          Se agrega al final de la línea de tiempo (posición {siguienteOrden + 1}).
        </p>

        <form onSubmit={handleSubmit} className="space-y-3">
          <div className="space-y-1">
            <Label className="text-xs">Título</Label>
            <Input
              value={form.nombre}
              onChange={(e) => setForm((f) => ({ ...f, nombre: e.target.value }))}
              required
              maxLength={120}
              placeholder="Ej. Inspección de aduana"
            />
          </div>

          <div className="space-y-1">
            <Label className="text-xs">Icono</Label>
            <div className="flex items-center gap-2">
              <span className="flex items-center justify-center h-10 w-10 shrink-0 rounded-md border border-input bg-muted/40 text-primary">
                <IconoPreview className="h-4 w-4" />
              </span>
              <Select
                value={form.icono}
                onValueChange={(v) => setForm((f) => ({ ...f, icono: v }))}
              >
                <SelectTrigger className="flex-1">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {ICON_OPTIONS.map((o) => {
                    const Ico = iconByName(o.value);
                    return (
                      <SelectItem key={o.value} value={o.value}>
                        <span className="flex items-center gap-2">
                          <Ico className="h-3.5 w-3.5" />
                          {o.label}
                        </span>
                      </SelectItem>
                    );
                  })}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="space-y-1">
            <Label className="text-xs">Descripción (opcional)</Label>
            <Textarea
              value={form.descripcion}
              onChange={(e) => setForm((f) => ({ ...f, descripcion: e.target.value }))}
              rows={3}
              maxLength={1000}
              placeholder="Notas, instrucciones, próximos pasos…"
            />
          </div>

          <label
            className={`flex items-start gap-2 rounded-md border p-2.5 transition-colors ${
              clienteSinCorreo
                ? 'border-input opacity-60 cursor-not-allowed'
                : form.notificar
                  ? 'border-primary bg-primary/5 cursor-pointer'
                  : 'border-input hover:bg-muted/40 cursor-pointer'
            }`}
            title={clienteSinCorreo ? 'El cliente no tiene correo registrado' : ''}
          >
            <input
              type="checkbox"
              checked={form.notificar}
              disabled={clienteSinCorreo}
              onChange={(e) => setForm((f) => ({ ...f, notificar: e.target.checked }))}
              className="h-4 w-4 mt-0.5"
            />
            <span>
              <span className="text-xs font-medium text-foreground flex items-center gap-1">
                <Mail className="h-3 w-3" />
                Informar al cliente
              </span>
              <span className="block text-[11px] text-muted-foreground">
                {clienteSinCorreo
                  ? 'El cliente no tiene correo registrado.'
                  : 'Le llega un correo con este hito y el link de seguimiento.'}
              </span>
            </span>
          </label>

          <Button type="submit" className="w-full" disabled={saving || !form.nombre.trim()}>
            {saving ? (
              <>
                <Loader2 className="h-4 w-4 mr-1.5 animate-spin" />
                Guardando…
              </>
            ) : (
              <>
                <PlusCircle className="h-4 w-4 mr-1.5" />
                Agregar hito
              </>
            )}
          </Button>
        </form>
      </CardContent>
    </Card>
  );
}

/** Diálogo de edición de un hito ya existente. */
export function StageEditDialog({ open, onOpenChange, initial, onSubmit }) {
  const [form, setForm] = useState({
    nombre: '',
    icono: 'Flag',
    descripcion: '',
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) {
      setForm({
        nombre: initial?.nombre || '',
        icono: initial?.icono || 'Flag',
        descripcion: initial?.descripcion || '',
      });
    }
  }, [open, initial]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.nombre.trim()) return;
    setSaving(true);
    try {
      await onSubmit({
        nombre: form.nombre.trim(),
        icono: form.icono,
        descripcion: form.descripcion.trim(),
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Editar hito</DialogTitle>
          <DialogDescription>
            Cambiá el título, el icono o la descripción del hito.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-3">
          <div className="space-y-1">
            <Label>Título</Label>
            <Input
              value={form.nombre}
              onChange={(e) => setForm((f) => ({ ...f, nombre: e.target.value }))}
              required
              autoFocus
              maxLength={120}
            />
          </div>
          <div className="space-y-1">
            <div className="space-y-1">
              <Label>Icono</Label>
              <Select
                value={form.icono}
                onValueChange={(v) => setForm((f) => ({ ...f, icono: v }))}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {ICON_OPTIONS.map((o) => {
                    const Ico = iconByName(o.value);
                    return (
                      <SelectItem key={o.value} value={o.value}>
                        <span className="flex items-center gap-2">
                          <Ico className="h-3.5 w-3.5" />
                          {o.label}
                        </span>
                      </SelectItem>
                    );
                  })}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="space-y-1">
            <Label>Descripción (opcional)</Label>
            <Textarea
              value={form.descripcion}
              onChange={(e) => setForm((f) => ({ ...f, descripcion: e.target.value }))}
              rows={3}
              maxLength={1000}
            />
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancelar
            </Button>
            <Button type="submit" disabled={saving || !form.nombre.trim()}>
              {saving ? 'Guardando…' : 'Guardar'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

/**
 * Diálogo de adjuntar archivo a un hito. El front primero pide una
 * presigned PUT al backend de R2, sube el archivo directo y luego
 * notifica al backend con la key.
 */
export function AttachFileDialog({ stage, onClose, onSubmit }) {
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState(null);

  const handleFile = (e) => {
    const f = e.target.files?.[0];
    if (f) {
      setFile(f);
      setError(null);
    }
  };

  const upload = async () => {
    if (!file) return;
    setUploading(true);
    setError(null);
    try {
      // 1) Pedir presigned URL
      const presignRes = await fetch(
        `${import.meta.env.VITE_API_URL || ''}/api/r2/upload-url`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            mimeType: file.type,
            size: file.size,
            scope: 'traceability',
          }),
        },
      );
      if (!presignRes.ok) {
        const j = await presignRes.json().catch(() => ({}));
        throw new Error(j?.message || `R2 no configurado (${presignRes.status})`);
      }
      const { uploadUrl, key } = await presignRes.json();

      // 2) Subir a R2 con PUT directo
      const putRes = await fetch(uploadUrl, {
        method: 'PUT',
        headers: { 'Content-Type': file.type },
        body: file,
      });
      if (!putRes.ok) {
        throw new Error(`Falló la subida a R2 (${putRes.status})`);
      }

      // 3) Notificar al backend de la trazabilidad
      await onSubmit({
        nombreOriginal: file.name,
        nombreInterno: key.split('/').pop() || file.name,
        mimeType: file.type,
        tamanoBytes: file.size,
        r2Key: key,
        urlPublica: null,
      });
    } catch (err) {
      setError(err?.message || 'Error al subir el archivo');
    } finally {
      setUploading(false);
    }
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Adjuntar archivo al hito</DialogTitle>
          <DialogDescription>
            Hito: <strong>{stage?.nombre}</strong>
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <Input
            type="file"
            onChange={handleFile}
            accept="application/pdf,image/*,.doc,.docx,.xls,.xlsx,.txt,.csv"
          />
          {file && (
            <p className="text-xs text-muted-foreground">
              {file.name} · {(file.size / 1024).toFixed(1)} KB
            </p>
          )}
          {error && (
            <div className="text-sm text-rose-700 bg-rose-50 border border-rose-200 rounded-md px-3 py-2">
              {error}
            </div>
          )}
          <p className="text-[11px] text-muted-foreground">
            Si tu operador no tiene R2 configurado, la subida no funcionará. El
            archivo queda en la nube del operador y se incluye un link de
            descarga para el cliente.
          </p>
        </div>
        <DialogFooter>
          <Button type="button" variant="outline" onClick={onClose}>
            Cancelar
          </Button>
          <Button onClick={upload} disabled={!file || uploading}>
            {uploading ? 'Subiendo…' : 'Subir y adjuntar'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
